import { Upload as Dragger } from '@/components/ui/Upload';
import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from './ui/Dialog';
import { Button } from './ui/Button';
import { apiService } from '../utils/api';
import { Cropper } from 'react-advanced-cropper';
import debounce from 'lodash/debounce';
import { convertFileToBase64 } from '../utils/image';
import classNames from 'classnames';
import 'react-advanced-cropper/dist/style.css';
import { toast } from 'sonner';

const ImageCropperDialog = ({
  url,
  visible,
  aspectRatio = 1,
  onSubmit,
  onClose,
  method = 'post',
}: {
  url: any;
  visible: any;
  aspectRatio?: any;
  onSubmit: any;
  onClose: any;
  method?: string;
}) => {
  const [, setLoading] = useState(false);
  const croppedImageFile = useRef<any>();
  const [cropperSource, setCropperSource] = useState(null);
  const [currentFileName, setCurrentFileName] = useState(null);

  const handleFileInputUpdate = async (file) => {
    const buffer = await convertFileToBase64(file);
    setCropperSource(buffer.toString());
  };

  const handleClose = () => {
    onClose();
    setCropperSource(undefined);
    setCurrentFileName(null);
  };

  const handleConfirm = () => {
    if (croppedImageFile.current) {
      upload();
      setCropperSource(undefined);
      setCurrentFileName(null);
    }
  };

  const handleCropperChange = debounce((cropper) => {
    cropper.getCanvas()?.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }

        croppedImageFile.current = new File([blob], currentFileName, {
          type: blob.type,
        });
      },
      undefined,
      1
    );
  }, 250);

  const handleCropperReady = (cropper) => {
    handleCropperChange(cropper);
  };

  const upload = async () => {
    const fmData = new FormData();
    const config = {
      headers: { 'content-type': 'multipart/form-data' },
    };

    fmData.append('file', croppedImageFile.current);

    setLoading(true);
    try {
      const res = await apiService({ method: method, url, data: fmData, ...config });

      if (res.data) {
        onSubmit({ ...res.data, title: currentFileName || res.data.title });
      } else {
        onSubmit(fmData);
      }
    } catch (err) {
      console.log('Eroor: ', err);
    } finally {
      setLoading(false);
    }
  };

  const update = async (options) => {
    const { file } = options;
    setCurrentFileName(file.name);
    await handleFileInputUpdate(file);
  };

  const config = {
    name: 'file',
    multiple: true,
    accept: 'image/*',
    customRequest: update,
    showUploadList: false,
    beforeUpload: (file) => {
      const fileSize = file.size;
      const fileMb = fileSize / 1024 ** 2;

      if (fileMb >= 20) {
        toast.error('Maximální velikost je 20MB');
        return false;
      }
      return true;
    },
  };

  return (
    <Dialog open={visible} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nahrát soubor</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col h-auto">
            {cropperSource && (
              <div>
                <Cropper
                  src={cropperSource}
                  onChange={handleCropperChange}
                  className={classNames('m-0 p-0', {
                    'h-[400px]': cropperSource,
                  })}
                  onReady={handleCropperReady}
                  stencilProps={{
                    grid: true,
                    movable: true,
                    aspectRatio: aspectRatio || 1,
                  }}
                  transitions={true}
                />
              </div>
            )}
            <Dragger {...config}>
              <p className="ant-upload-text p-5">Klikni nebo přetáhni soubor pro jeho nahrání</p>
            </Dragger>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            data-testid="image-cropper-close-btn"
            key={1}
            variant="outline"
            onClick={() => handleClose()}
          >
            Zavřít
          </Button>
          <Button data-testid="image-cropper-confirm-btn" key={2} onClick={() => handleConfirm()}>
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropperDialog;
